<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import { useCardBookmark } from "@/composables/useCardBookmark";
import { useCardTelemetry } from "@/composables/useCardTelemetry";
import isUnauth from "@/utils/isUnauth";

interface Props {
  result: any;
  //eslint-disable-next-line vue/no-unused-properties
  index?: number;
  status?: any;
  forCarousel?: boolean;
  cardView?: string;
  cardStyle?: string;
  topicView?: boolean;
  isStoreContent?: boolean;
  showDescription?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  index: 0,
  status: null,
  forCarousel: false,
  cardView: "Grid",
  cardStyle: "Grid",
  topicView: false,
  isStoreContent: false,
  showDescription: true,
});

const router = useRouter();

// Composables
const {
  bookmarkMe,
  debouncedBookmarkMe,
  addContentToMyList,
  removeContentFromMyList,
} = useCardBookmark();

const { sendTelemetry: sendTelemetryBase } = useCardTelemetry();

// Computed properties
const resultLink = computed(() => {
  const challengeId = props.result?.friendly_url || props.result?.id;

  return challengeId
    ? router.resolve({ name: "Challenge", params: { challengeId } }).href
    : "";
});

// Methods
const sendTelemetry = async () => {
  await sendTelemetryBase(props, resultLink.value);
};

defineExpose({
  addContentToMyList,
  removeContentFromMyList,
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  resultLink,
});
</script>

<template>
  <div
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
    data-cy="challenge-result"
    v-show="result"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'challenge-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
    <the-productCard
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="'USD'"
      :result="result"
      :status="status"
      :cy="'challenge-product-card'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="sendTelemetry"
      @bookmark="debouncedBookmarkMe(result)"
    />
  </div>
</template>
